import React, { useEffect, useState } from "react";
import { api, formatPKR } from "@/lib/api";
import { Calendar as CalendarIcon, TrendingUp, Package, Truck, Clock, CheckCircle2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

export default function AdminDashboard() {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (start) params.start = fmtDate(start);
      if (end) params.end = fmtDate(end) + "T23:59:59";
      const r = await api.get("/admin/analytics", { params });
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end]);

  const stats = [
    { label: "Total Revenue", value: formatPKR(data?.total_revenue || 0), icon: TrendingUp, color: "text-sage" },
    { label: "Orders", value: data?.total_orders || 0, icon: Package, color: "text-ink" },
    { label: "Avg Order Value", value: formatPKR(data?.average_order_value || 0), icon: TrendingUp, color: "text-rose" },
    { label: "Pending", value: data?.status_counts?.Pending || 0, icon: Clock, color: "text-ink-soft" },
  ];

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row justify-between sm:items-end gap-4 mb-8">
        <div>
          <div className="label-eyebrow mb-1">Dashboard</div>
          <h1 className="font-display text-4xl text-ink">Sales & Analysis</h1>
        </div>
        <div className="flex items-center gap-2">
          <DateButton label="Start" date={start} setDate={setStart} testid="filter-start" />
          <DateButton label="End" date={end} setDate={setEnd} testid="filter-end" />
          {(start || end) && (
            <Button variant="ghost" onClick={() => { setStart(null); setEnd(null); }} className="text-xs" data-testid="filter-clear">
              Clear
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-border-subtle p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-start justify-between">
              <div className="label-eyebrow !text-[10px]">{s.label}</div>
              <s.icon size={16} className={s.color} />
            </div>
            <div className="mt-3 font-display text-3xl text-ink">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        <div className="lg:col-span-2 bg-white border border-border-subtle p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-2xl text-ink">Revenue Trend</h3>
            <span className="label-eyebrow !text-[10px]">{loading ? "Loading…" : "Daily"}</span>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.daily_series || []}>
                <defs>
                  <linearGradient id="sageGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8DA08B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#8DA08B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="#5C635A" fontSize={11} />
                <YAxis stroke="#5C635A" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#FDFBF7", border: "1px solid #E5E1D8", borderRadius: 0 }}
                  formatter={(v) => formatPKR(v)}
                />
                <Area type="monotone" dataKey="revenue" stroke="#8DA08B" strokeWidth={2} fill="url(#sageGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-border-subtle p-5">
          <h3 className="font-display text-2xl text-ink mb-4">Order Status</h3>
          <div className="space-y-3">
            {[
              { k: "Pending", color: "bg-amber-500/80", icon: Clock },
              { k: "Confirmed", color: "bg-sage", icon: CheckCircle2 },
              { k: "Shipped", color: "bg-rose", icon: Truck },
              { k: "Delivered", color: "bg-ink", icon: Package },
            ].map((s) => {
              const v = data?.status_counts?.[s.k] || 0;
              const total = data?.total_orders || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={s.k} data-testid={`status-${s.k.toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-ink flex items-center gap-2"><s.icon size={12} /> {s.k}</span>
                    <span className="text-ink-soft">{v}</span>
                  </div>
                  <div className="h-1.5 bg-cream-warm overflow-hidden">
                    <div className={`h-full ${s.color} transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white border border-border-subtle p-5">
        <h3 className="font-display text-2xl text-ink mb-4">Revenue by Category</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.category_breakdown || []}>
              <CartesianGrid stroke="#E5E1D8" strokeDasharray="3 3" />
              <XAxis dataKey="category" stroke="#5C635A" fontSize={11} />
              <YAxis stroke="#5C635A" fontSize={11} />
              <Tooltip contentStyle={{ background: "#FDFBF7", border: "1px solid #E5E1D8" }} formatter={(v) => formatPKR(v)} />
              <Bar dataKey="revenue" fill="#C98E8E" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function DateButton({ label, date, setDate, testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="border-border-subtle text-ink-soft hover:text-ink" data-testid={testid}>
          <CalendarIcon size={14} className="mr-2" />
          {date ? fmtDate(date) : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-white border-border-subtle">
        <Calendar mode="single" selected={date} onSelect={setDate} />
      </PopoverContent>
    </Popover>
  );
}
